import {
  useGetBuildingPackagesQuery,
  useGetPackagesQuery,
  useGetLayoutOptimizationsQuery,
  useDownloadBuildingPackageZip,
  useUpdatePackageMutation,
  useUpdateLayoutOptimizationMutation,
} from '@consigli/hooks';
import { Project, ServiceEntityType } from '@consigli/types';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { ModalAlert, DataTable, ObjectDetailView } from '@/components';
import { errorToast, successToast } from '@/components/toast';
import {
  getServiceType,
  isBuildingPackage,
  isDocumentPackage,
  isLayoutOptimization,
} from '@/utils/type-guards';
import { Service } from '@/utils/types';

import { createServiceColumns } from './columns';
import { useDeleteService } from './use-delete-service';
import { useDownloadDocumentsExcel } from './use-download-documents-excel';
import { useDownloadPackageZip } from './use-download-package-zip';

interface ServiceTableProps {
  project: Project;
}

/**
 * Service Table for displaying all services of a project.
 * @param project - The project.
 * @returns The service table.
 */
export const ServiceTable = ({ project }: ServiceTableProps) => {
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const navigate = useNavigate();

  const { data: documentPackages = [], isLoading: isLoadingDocumentPackages } = useGetPackagesQuery(
    { projectId: project.id },
  );
  const { data: layoutOptimizations = [], isLoading: isLoadingLayoutOptimizations } =
    useGetLayoutOptimizationsQuery({ projectId: project.id });
  const { data: buildingPackages = [], isLoading: isLoadingBuildingPackages } =
    useGetBuildingPackagesQuery({ projectId: project.id, page: 'all' });

  const buildingPackagesArray = Array.isArray(buildingPackages)
    ? buildingPackages
    : buildingPackages.results;

  const isLoading =
    isLoadingDocumentPackages || isLoadingLayoutOptimizations || isLoadingBuildingPackages;

  const data: Service[] = [...documentPackages, ...layoutOptimizations, ...buildingPackagesArray];

  const {
    isModalOpen,
    serviceToDelete,
    isDeleting,
    handleDeleteClick,
    handleConfirmDelete,
    handleCloseModal,
  } = useDeleteService();

  const { downloadPackageAsExcel } = useDownloadDocumentsExcel();
  const { downloadPackageAsZip } = useDownloadPackageZip();
  const { downloadBuildingPackageAsZip } = useDownloadBuildingPackageZip();

  // Toggle visibility functionality
  const [updatePackage, { isLoading: isTogglingDocPkg }] = useUpdatePackageMutation();
  const [updateLayoutOptimization, { isLoading: isTogglingLayoutOpt }] =
    useUpdateLayoutOptimizationMutation();
  const isTogglingVisibility = isTogglingDocPkg || isTogglingLayoutOpt;

  const handleToggleVisibilityClick = useCallback(
    async (service: Service) => {
      if (!('published' in service)) return;
      const published = !service.published;
      try {
        if (isDocumentPackage(service)) {
          await updatePackage({
            projectId: project.id,
            packageId: service.id,
            data: { published },
            isHidden: service.isHidden,
          }).unwrap();
        } else if (isLayoutOptimization(service)) {
          await updateLayoutOptimization({
            projectId: project.id,
            layoutOptimizationId: service.id,
            data: { published },
            isHidden: service.isHidden,
          }).unwrap();
        }
        successToast({
          message: published
            ? `Service "${service.name}" is now published`
            : `Service "${service.name}" is now unpublished`,
        });
      } catch (error) {
        console.error('Failed to update service publish status:', error);
        errorToast({ message: 'Failed to update service publish status' });
      }
    },
    [project.id, updatePackage, updateLayoutOptimization],
  );

  const handleViewDetailsClick = (service: Service) => {
    setSelectedService(service);
    setIsDetailViewOpen(true);
  };

  const handleDownloadZipClick = async (service: Service) => {
    if (isDocumentPackage(service)) {
      try {
        await downloadPackageAsZip(service, project.id);
      } catch (error) {
        console.error('Failed to download package as ZIP:', error);
      }
    }
    if (isBuildingPackage(service)) {
      try {
        await downloadBuildingPackageAsZip(service, project.id);
      } catch (error) {
        console.error('Failed to download building package as ZIP:', error);
      }
    }
  };

  const handleDownloadExcelClick = async (service: Service) => {
    if (isDocumentPackage(service)) {
      try {
        await downloadPackageAsExcel(service, project.id);
      } catch (error) {
        console.error('Failed to download package as Excel:', error);
      }
    }
  };

  const handleRowClick = (service: Service) => {
    const serviceType = getServiceType(service);

    switch (serviceType) {
      case ServiceEntityType.DOCUMENT_PACKAGE:
        navigate(`/projects/${project.id}/Packages/${service.id}`);
        break;
      case ServiceEntityType.LAYOUT_OPTIMIZATION:
        navigate(`/projects/${project.id}/Layouts/${service.id}`);
        break;
      case ServiceEntityType.BUILDING_PACKAGE:
        navigate(`/projects/${project.id}/Buildings/${service.id}`);
        break;
      default:
        console.warn(`Unknown service type: ${serviceType}`);
    }
  };

  const columns = createServiceColumns({
    onDeleteClick: (service: Service) => handleDeleteClick(service, project.id),
    onViewDetailsClick: handleViewDetailsClick,
    onToggleVisibilityClick: handleToggleVisibilityClick,
    onDownloadExcelClick: handleDownloadExcelClick,
    onDownloadZipClick: handleDownloadZipClick,
    isTogglingVisibility,
    isDeleting,
  });

  return (
    <>
      <DataTable columns={columns} data={data} isLoading={isLoading} onRowClick={handleRowClick} />

      <ModalAlert
        isOpen={isModalOpen}
        onOpenChange={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Delete Service"
        description={
          serviceToDelete
            ? `Are you sure you want to delete the service "${serviceToDelete.service.name}"? This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        loadingText="Deleting..."
        destructive={true}
      />

      {selectedService && (
        <ObjectDetailView
          object={selectedService}
          objectType="Service"
          summary={`Service "${selectedService.name}" details including type, status, and configuration.`}
          isOpen={isDetailViewOpen}
          onOpenChange={(open: boolean) => {
            setIsDetailViewOpen(open);
            if (!open) {
              setSelectedService(null);
            }
          }}
        />
      )}
    </>
  );
};
